// Generate HTML for the email signature
export const generateSignatureHTML = (data) => {
  const getTextStyle = (styles) => {
    let style = '';
    if (styles?.bold) style += 'font-weight: bold;';
    if (styles?.italic) style += 'font-style: italic;';
    if (styles?.underline) style += 'text-decoration: underline;';
    return style;
  };

  const getSizeStyles = () => {
    switch (data.templateSize) {
      case 'pequeno':
        return { nameFontSize: '14px', textFontSize: '11px', iconSize: '12', padding: '12px' };
      case 'grande':
        return { nameFontSize: '20px', textFontSize: '14px', iconSize: '18', padding: '24px' };
      default:
        return { nameFontSize: '16px', textFontSize: '12px', iconSize: '14', padding: '16px' };
    }
  };

  const sizes = getSizeStyles();
  
  const templateConfig = {
    leftBorder: ['classico', 'moderno', 'negrito'].includes(data.templateStyle),
    divider: ['minimalista', 'moderno', 'profissional', 'elegante'].includes(data.templateStyle),
    logoPosition: data.templateStyle === 'criativo' ? 'top' : 
                  data.templateStyle === 'profissional' ? 'right' : 'left',
  };

  const socialLinks = [
    { key: 'facebook', url: data.facebook, icon: 'F' },
    { key: 'twitter', url: data.twitter, icon: 'X' },
    { key: 'linkedin', url: data.linkedin, icon: 'in' },
    { key: 'instagram', url: data.instagram, icon: 'IG' },
    { key: 'youtube', url: data.youtube, icon: 'YT' },
    { key: 'tiktok', url: data.tiktok, icon: 'TT' },
  ].filter(s => s.url);

  const customLinksFiltered = data.customLinks?.filter(l => l.label && l.url) || [];

  let html = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: ${data.fontFamily}, Arial, sans-serif; background-color: ${data.backgroundColor}; ${templateConfig.leftBorder ? `border-left: 4px solid ${data.primaryColor};` : ''} padding: ${sizes.padding};">
  <tr>
    <td style="vertical-align: top;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>`;

  // Logo (left/top)
  if (data.logoPreview && (templateConfig.logoPosition === 'left' || templateConfig.logoPosition === 'top')) {
    const logoSize = data.templateSize === 'pequeno' ? '50' : data.templateSize === 'grande' ? '80' : '64';
    if (templateConfig.logoPosition === 'top') {
      html += `
        </tr>
        <tr>
          <td align="center" style="padding-bottom: 12px;">
            <img src="${data.logoPreview}" alt="Logo" width="${logoSize}" style="border-radius: ${data.logoBorderRadius}%; display: block;" />
          </td>
        </tr>
        <tr>`;
    } else {
      html += `
          <td style="vertical-align: top; padding-right: 16px;">
            <img src="${data.logoPreview}" alt="Logo" width="${logoSize}" style="border-radius: ${data.logoBorderRadius}%; display: block;" />
          </td>`;
    }
  }

  // Content
  html += `
          <td style="vertical-align: top;">`;

  // Name
  if (data.fullName) {
    html += `
            <div style="color: ${data.primaryColor}; font-size: ${sizes.nameFontSize}; margin-bottom: 4px; ${getTextStyle(data.fullNameStyles)}">${data.fullName}</div>`;
  }

  // Position & Company
  if (data.position || data.company) {
    html += `
            <div style="color: ${data.secondaryColor}; font-size: ${sizes.textFontSize}; margin-bottom: 8px;">`;
    if (data.position) {
      html += `<span style="${getTextStyle(data.positionStyles)}">${data.position}</span>`;
    }
    if (data.position && data.company) html += ' | ';
    if (data.company) {
      html += `<span style="${getTextStyle(data.companyStyles)}">${data.company}</span>`;
    }
    html += `</div>`;
  }

  // Divider
  if (templateConfig.divider) {
    html += `
            <div style="height: 1px; background-color: ${data.secondaryColor}; opacity: 0.3; margin: 8px 0;"></div>`;
  }

  // Contact Info
  const contacts = [];
  if (data.email) contacts.push(`<a href="mailto:${data.email}" style="color: ${data.secondaryColor}; text-decoration: none;">${data.email}</a>`);
  if (data.phone) contacts.push(`<a href="tel:${data.phone.replace(/\s/g, '')}" style="color: ${data.secondaryColor}; text-decoration: none;">${data.phone}</a>`);
  if (data.website) {
    const websiteUrl = data.website.startsWith('http') ? data.website : `https://${data.website}`;
    contacts.push(`<a href="${websiteUrl}" style="color: ${data.secondaryColor}; text-decoration: none;">${data.website}</a>`);
  }
  
  if (contacts.length > 0) {
    html += `
            <div style="font-size: ${sizes.textFontSize}; margin-bottom: 8px;">
              ${contacts.join(' &nbsp;|&nbsp; ')}
            </div>`;
  }

  // Social Links
  if (socialLinks.length > 0) {
    html += `
            <div style="margin-top: 8px;">`;
    socialLinks.forEach(({ url, icon }) => {
      const socialUrl = url.startsWith('http') ? url : `https://${url}`;
      html += `
              <a href="${socialUrl}" style="display: inline-block; width: 24px; height: 24px; background-color: ${data.accentColor}; color: white; text-align: center; line-height: 24px; border-radius: 4px; margin-right: 6px; text-decoration: none; font-size: 10px; font-weight: bold;">${icon}</a>`;
    });
    html += `
            </div>`;
  }

  // Custom Links
  if (customLinksFiltered.length > 0) {
    html += `
            <div style="margin-top: 8px; font-size: ${sizes.textFontSize};">`;
    customLinksFiltered.forEach((link) => {
      const linkUrl = link.url.startsWith('http') ? link.url : `https://${link.url}`;
      html += `
              <a href="${linkUrl}" style="color: ${data.accentColor}; text-decoration: none; margin-right: 12px;">→ ${link.label}</a>`;
    });
    html += `
            </div>`;
  }

  html += `
          </td>`;

  // Logo (right)
  if (data.logoPreview && templateConfig.logoPosition === 'right') {
    const logoSize = data.templateSize === 'pequeno' ? '50' : data.templateSize === 'grande' ? '80' : '64';
    html += `
          <td style="vertical-align: top; padding-left: 16px;">
            <img src="${data.logoPreview}" alt="Logo" width="${logoSize}" style="border-radius: ${data.logoBorderRadius}%; display: block;" />
          </td>`;
  }

  html += `
        </tr>
      </table>`;

  // Banner
  if (data.bannerPreview) {
    html += `
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top: 16px;">
        <tr>
          <td>
            <img src="${data.bannerPreview}" alt="Banner" width="400" style="border-radius: ${data.bannerBorderRadius}%; display: block; max-width: 100%;" />
          </td>
        </tr>
      </table>`;
  }

  html += `
    </td>
  </tr>
</table>`;

  return html.trim();
};

// Copy as rich text (formatted)
export const copyAsRichText = async (data) => {
  const html = generateSignatureHTML(data);
  
  try {
    // Try to use the modern Clipboard API with HTML
    if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
      const blob = new Blob([html], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      await navigator.clipboard.write([clipboardItem]);
      return true;
    } else {
      // Fallback: create a hidden element and copy
      const container = document.createElement('div');
      container.innerHTML = html;
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      document.body.appendChild(container);
      
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(container);
      selection.removeAllRanges();
      selection.addRange(range);
      
      document.execCommand('copy');
      document.body.removeChild(container);
      return true;
    }
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

// Copy HTML source
export const copyHTMLSource = async (data) => {
  const html = generateSignatureHTML(data);
  
  try {
    await navigator.clipboard.writeText(html);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    // Fallback
    const textarea = document.createElement('textarea');
    textarea.value = html;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return true;
  }
};

// Export configuration as JSON
export const exportConfig = (data) => {
  const exportData = { ...data };
  // Remove file objects, keep only previews
  delete exportData.logo;
  delete exportData.banner;
  delete exportData.onChange;
  
  const json = JSON.stringify(exportData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = 'assinatura-email-config.json';
  a.click();
  
  URL.revokeObjectURL(url);
};

// Import configuration from JSON
export const importConfig = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const config = JSON.parse(e.target.result);
        resolve(config);
      } catch (err) {
        reject(new Error('Arquivo JSON inválido'));
      }
    };
    reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
    reader.readAsText(file);
  });
};
